import React, { useState, forwardRef } from "react";
import { Dialog, Typography, Button, IconButton, Divider, Slide } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import dayjs from "~/config/dayjs";
import checkUserPackageSessionsAction from "~/store/actions/checkUserPackageSessionsAction";
import Utils from "~/helpers/Utils";
import { formatDateDDMMYYYY } from "~/utils/packages";
import usePolicyActive from "~/hooks/usePolicyActive";
import useDateFormatter from "~/hooks/useDateFormatter";
import useBottomSheetClasses, { BOTTOM_SHEET_FIXED_CLASS } from "~/components/common/bottomSheet/useBottomSheetClasses";

const SlideUp = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const CheckoutSessionPackageModal = ({
	open,
	sessions,
	service,
	onUsePackage,
	onSkip,
	currentStep = 1,
	totalSteps = 1,
	currency,
}) => {
	const { t, i18n } = useTranslation();
	const dispatch = useDispatch();
	const [selectedId, setSelectedId] = useState(null);
	const isActivePolicyCancellation = usePolicyActive();
	const { formatShortTime, formatDateToLanguage } = useDateFormatter();
	const sheetClasses = useBottomSheetClasses();

	if (!sessions || sessions.length === 0 || !service) return null;

	const isMultiple = sessions.length > 1;
	const activeSession = isMultiple
		? sessions.find((s) => s.bookingTransactionId === selectedId) || null
		: sessions[0];

	const date = service?.date ? dayjs(service.date).format(formatDateToLanguage().dayMonth) : "";
	const time = service?.date ? formatShortTime(service.date) : "";
	const standalonePrice = service?.service?.servicePrice || service?.professional?.price || 0;
	const serviceNameFallback = service?.service?.serviceName || "";
	const isLastStep = currentStep >= totalSteps;
	const showStepIndicator = totalSteps > 1;

	const buildSessionData = (session) => ({
		bookingId: session.sessions[0].id,
		serviceId: service.service_id,
		payload: {
			start_time: service.date,
			end_time: dayjs(service.date).add(service?.professional?.duration, "minute").toISOString(),
			acesso: "web",
			answer: "",
		},
		infoPackage: {
			packageName: session.package?.name,
			packageQuantity: session.package?.quantity,
			packageRemainding: session.remainingSessions,
			servicePricePackage: session.sessions[0]?.price,
			paymentStatus: session.paymentStatus,
			packageTotalPrice: session.sessions.reduce((sum, s) => sum + parseFloat(s.price || 0), 0),
			packageExpiry: session.sessions[0]?.expiresAt
				? formatDateDDMMYYYY(session.sessions[0].expiresAt, i18n.language)
				: null,
		},
		services: [service],
	});

	const handleUsePackage = () => {
		const session = activeSession;
		if (!session) return;
		const sessionData = buildSessionData(session);
		dispatch(checkUserPackageSessionsAction.sessionsAccepted([sessionData]));
		onUsePackage(sessionData);
		setSelectedId(null);
	};

	const handleSkip = () => {
		setSelectedId(null);
		onSkip();
	};

	return (
		<Dialog
			open={open}
			onClose={handleSkip}
			TransitionComponent={SlideUp}
			transitionDuration={350}
			className={BOTTOM_SHEET_FIXED_CLASS}
			classes={{ container: sheetClasses.container }}
			PaperProps={{
				style: {
					width: "100%",
					maxWidth: 480,
					margin: 0,
					borderTopLeftRadius: 18,
					borderTopRightRadius: 18,
					borderBottomLeftRadius: 0,
					borderBottomRightRadius: 0,
					backgroundColor: "#FFFEF6",
				},
			}}
			BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.5)" } }}>
			<div style={{ padding: "20px 20px 28px" }}>
				{/* Header */}
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
					<Typography style={{ fontSize: 18, fontWeight: 700, flex: 1, paddingRight: 8, lineHeight: 1.3 }}>
						{t("package_offer_title_use")}
					</Typography>
					<IconButton size="small" onClick={handleSkip} style={{ padding: 4 }}>
						<Close style={{ fontSize: 22 }} />
					</IconButton>
				</div>

				{/* Service + step info */}
				<div style={{ marginBottom: 4 }}>
					<span style={{ fontSize: 14, fontWeight: 700, color: "#222" }}>{serviceNameFallback}</span>
					{showStepIndicator && (
						<span style={{ fontSize: 13, color: "rgba(0,0,0,0.5)", marginLeft: 8 }}>
							{t("checkout_session_modal_step_inline", { step: currentStep, total: totalSteps })}
						</span>
					)}
				</div>
				<Typography style={{ fontSize: 13, color: "rgba(0,0,0,0.55)", marginBottom: 20 }}>
					{date && time ? t("package_offer_date_time", { date, time }) : date || time}
				</Typography>

				{/* Package list — always radio-based */}
				<div style={{ maxHeight: 320, overflowY: "auto", marginBottom: 20, display: "flex", flexDirection: "column", gap: 10 }}>
					{sessions.map((session) => {
						const isSelected = !isMultiple || selectedId === session.bookingTransactionId;
						const isPending = session.paymentStatus === "pending" && isActivePolicyCancellation;
						const packageName = session.package?.name || session.service?.name || "";
						const remaining = session.remainingSessions ?? 0;
						const total = session.package?.quantity ?? 0;
						const professionalName = session.professional?.name || "";
						const activatedAt = session.createdAt ? dayjs(session.createdAt).format("DD/MM/YYYY") : "";
						const totalPrice = session.sessions
							? session.sessions.reduce((sum, s) => sum + parseFloat(s.price || 0), 0)
							: 0;

						return (
							<button
								key={session.bookingTransactionId}
								onClick={() => isMultiple && setSelectedId(session.bookingTransactionId)}
								style={{
									width: "100%",
									display: "flex",
									alignItems: "flex-start",
									gap: 12,
									background: isPending ? "#FFF8F8" : "#fff",
									border: `1px solid ${isSelected ? "#672E37" : isPending ? "#EBD3D8" : "#e0e0e0"}`,
									boxShadow: isSelected ? "0 0 0 1px #672E37" : "none",
									borderRadius: 12,
									padding: "13px 14px",
									cursor: isMultiple ? "pointer" : "default",
									fontFamily: "inherit",
									textAlign: "left",
								}}>
								{/* Radio indicator */}
								<div
									style={{
										width: 20,
										height: 20,
										borderRadius: "50%",
										flexShrink: 0,
										marginTop: 2,
										border: `1.8px solid ${isSelected ? "#672E37" : "#C6C6C0"}`,
										background: isSelected ? "#672E37" : "transparent",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}>
									{isSelected && (
										<svg width="10" height="10" viewBox="0 0 24 24" fill="none">
											<path d="M5 12.5l4.5 4.5L19 7" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									)}
								</div>

								<div style={{ flex: 1, minWidth: 0 }}>
									<div style={{ fontSize: 11, fontWeight: 700, color: "#999", letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>
										{t("package_offer_your_package")}
									</div>
									<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
										<Typography style={{ flex: 1, fontSize: 14.5, fontWeight: 700, color: "#2B2B30", lineHeight: 1.25 }}>
											{packageName}
										</Typography>
										{isPending && (
											<span style={{
												background: "#fff",
												border: "1px solid #EBD3D8",
												color: "#672E37",
												borderRadius: 999,
												padding: "3px 9px",
												fontSize: 11.5,
												fontWeight: 700,
												flexShrink: 0,
											}}>
												{t("checkout_session_pending_payment")}
											</span>
										)}
									</div>
									<Typography style={{ fontSize: 13, color: isPending ? "#7A4A55" : "rgba(0,0,0,0.55)", marginTop: 3 }}>
										{t("package_offer_available_sessions", { remaining, total })}
									</Typography>
									<Typography style={{ fontSize: 12.5, color: "rgba(0,0,0,0.45)", marginTop: 3 }}>
										{t("checkout_session_with_professional", { name: professionalName })}
										{activatedAt ? ` · ${t("checkout_session_activated_at", { date: activatedAt })}` : ""}
									</Typography>
									{isPending && totalPrice > 0 && (
										<Typography style={{ fontSize: 13, color: "#672E37", fontWeight: 600, marginTop: 5 }}>
											{Utils.formatMoney(totalPrice / 100, currency)} {t("checkout_session_pending_pay_before_use")}
										</Typography>
									)}
								</div>
							</button>
						);
					})}
				</div>

				<Divider style={{ marginBottom: 16 }} />

				{/* Action buttons: "Não usar o pacote" com o preço avulso à esquerda, "Usar pacote" à direita */}
				<div style={{ display: "flex", flexDirection: "row", gap: 10 }}>
					<Button
						variant="outlined"
						onClick={handleSkip}
						style={{
							backgroundColor: "#F5F5F0",
							color: "rgba(0,0,0,0.75)",
							borderRadius: 30,
							minHeight: 40,
							padding: "6px 0",
							textTransform: "none",
							border: "1px solid #e0e0e0",
							boxShadow: "none",
							flex: 1,
						}}>
						<span style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.3 }}>
							<span style={{ fontSize: 15, fontWeight: 600 }}>{t("package_offer_dont_use_package")}</span>
							{standalonePrice > 0 && (
								<span style={{ fontSize: 12, fontWeight: 400, color: "#888" }}>
									{Utils.formatMoney(standalonePrice / 100, currency)}
								</span>
							)}
						</span>
					</Button>
					<Button
						variant="contained"
						disabled={isMultiple && !selectedId}
						onClick={handleUsePackage}
						style={{
							backgroundColor: isMultiple && !selectedId ? "#D8C0C7" : "#672E37",
							color: "#fff",
							fontWeight: 700,
							fontSize: 15,
							borderRadius: 30,
							minHeight: 40,
							textTransform: "none",
							boxShadow: "none",
							flex: 1,
						}}>
						{isLastStep
							? t("package_offer_use_package")
							: t("checkout_session_use_package_continue")}
					</Button>
				</div>
			</div>
		</Dialog>
	);
};

export default CheckoutSessionPackageModal;
